import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Plus, Trash2 } from "lucide-react";

import { StepShell, FormField } from "./StepShell";

import { LOAN_TYPES } from "@/constants/loanTypes";

const EMPTY_LOAN = {
  applicantType: "PRIMARY",
  customerId: "",
  loanAccountNumber: "",
  bankName: "",
  loanType: "",
  status: "",
  loanAmount: "",
  interestRate: "",
  emi: "",
  outstandingBalance: "",
  residualTenureMonths: "",
  disbursementDate: "",
  remarks: "",
};

function createLocalId() {
  return `portal-loan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Normalize API data for the form.
 *
 * Important:
 * HTML date inputs require YYYY-MM-DD.
 *
 * The API/database may return:
 * - YYYY-MM-DD
 * - YYYY-MM-DDTHH:mm:ss
 * - YYYY-MM-DDTHH:mm:ss.sssZ
 */
function normalizeDate(value) {
  if (!value) {
    return "";
  }

  const stringValue = String(value);

  return stringValue.length >= 10 ? stringValue.slice(0, 10) : "";
}

function normalizeLoan(loan = {}) {
  return {
    ...EMPTY_LOAN,

    ...loan,

    applicantType: loan.applicantType || "PRIMARY",

    bankName: loan.bankName ?? loan.lenderName ?? loan.bank ?? "",

    loanType: loan.loanType ?? loan.loan_type ?? "",

    outstandingBalance: loan.outstandingBalance ?? loan.outstanding ?? "",

    residualTenureMonths:
      loan.residualTenureMonths ??
      loan.tenureRemaining ??
      loan.tenure_remaining ??
      "",

    disbursementDate: normalizeDate(
      loan.disbursementDate ?? loan.disbursement_date,
    ),

    _portalId: loan._portalId || loan.id || createLocalId(),
  };
}

/**
 * Convert the form object into the exact API contract.
 *
 * IMPORTANT:
 * Empty values are intentionally NOT removed.
 *
 * Text fields -> ""
 * Numeric/date fields -> null
 *
 * This means clearing a field actually clears the
 * existing database value instead of preserving it.
 */
function toApiLoan(loan) {
  return {
    applicantType: loan.applicantType || "PRIMARY",

    customerId: loan.customerId ?? "",

    loanAccountNumber: loan.loanAccountNumber ?? "",

    bankName: loan.bankName ?? "",

    loanType: loan.loanType ?? "",

    status: loan.status ?? "",

    loanAmount: loan.loanAmount === "" ? null : (loan.loanAmount ?? null),

    interestRate: loan.interestRate === "" ? null : (loan.interestRate ?? null),

    emi: loan.emi === "" ? null : (loan.emi ?? null),

    outstandingBalance:
      loan.outstandingBalance === "" ? null : (loan.outstandingBalance ?? null),

    residualTenureMonths:
      loan.residualTenureMonths === ""
        ? null
        : (loan.residualTenureMonths ?? null),

    disbursementDate: loan.disbursementDate || null,

    remarks: loan.remarks ?? "",
  };
}

export default function ExistingLoansStep({ state, patch, setStep }) {
  const [loans, setLoans] = useState(() =>
    Array.isArray(state?.existing_loans)
      ? state.existing_loans.map(normalizeLoan)
      : [],
  );

  useEffect(() => {
    setLoans(
      Array.isArray(state?.existing_loans)
        ? state.existing_loans.map(normalizeLoan)
        : [],
    );
  }, [state?.existing_loans]);

  const save = async () => {
    /*
     * The complete array represents the complete current
     * state of the Existing Loans step.
     *
     * Empty values are intentionally preserved.
     */
    const payload = loans.map(toApiLoan);

    await patch("existing_loans", payload);

    await setStep(4);
  };

  const add = () => {
    setLoans((current) => [
      ...current,
      {
        ...EMPTY_LOAN,
        _portalId: createLocalId(),
      },
    ]);
  };

  const remove = (id) => {
    setLoans((current) => current.filter((loan) => loan._portalId !== id));
  };

  const update = (id, field, value) => {
    setLoans((current) =>
      current.map((loan) =>
        loan._portalId === id
          ? {
              ...loan,
              [field]: value,
            }
          : loan,
      ),
    );
  };

  return (
    <StepShell
      testId="portal-existing-loans"
      title="Existing Loans (if any)"
      subtitle="Add all current loans and credit obligations. Leave this section empty if you have none."
      onBack={() => setStep(2)}
      onNext={save}
      nextLabel="Save & Continue"
    >
      {loans.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No existing loans added. Skip if you have none.
        </div>
      )}

      <div className="space-y-5">
        {loans.map((loan, index) => (
          <div
            key={loan._portalId}
            data-testid={`loan-row-${index}`}
            className="rounded-2xl border border-gray-200 p-5 bg-[#F8FAFC]"
          >
            <div className="flex justify-between items-center mb-4">
              <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-500">
                Loan #{index + 1}
              </span>

              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => remove(loan._portalId)}
                className="h-8 w-8 rounded-full"
              >
                <Trash2 className="h-4 w-4 text-gray-500" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField label="Applicant">
                <Select
                  value={loan.applicantType || ""}
                  onValueChange={(value) =>
                    update(loan._portalId, "applicantType", value)
                  }
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select applicant" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="PRIMARY">
                      {state?.personal_details?.fullName ||
                        state?.personal_details?.applicant_name ||
                        "Primary Applicant"}
                    </SelectItem>

                    {state?.co_applicant && (
                      <SelectItem value="CO_APPLICANT">
                        {state.co_applicant.fullName ||
                          state.co_applicant.applicant_name ||
                          "Co-Applicant"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Bank / NBFC">
                <Input
                  data-testid={`loan-bank-${index}`}
                  value={loan.bankName}
                  onChange={(event) =>
                    update(loan._portalId, "bankName", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Customer ID">
                <Input
                  value={loan.customerId}
                  onChange={(event) =>
                    update(loan._portalId, "customerId", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Loan Account Number">
                <Input
                  value={loan.loanAccountNumber}
                  onChange={(event) =>
                    update(
                      loan._portalId,
                      "loanAccountNumber",
                      event.target.value,
                    )
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Loan Type">
                <Input
                  data-testid={`loan-type-${index}`}
                  value={loan.loanType}
                  onChange={(event) =>
                    update(loan._portalId, "loanType", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Status">
                <Input
                  value={loan.status}
                  onChange={(event) =>
                    update(loan._portalId, "status", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Loan Amount (₹)">
                <Input
                  type="number"
                  min="0"
                  value={loan.loanAmount ?? ""}
                  onChange={(event) =>
                    update(loan._portalId, "loanAmount", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Interest Rate (%)">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={loan.interestRate ?? ""}
                  onChange={(event) =>
                    update(loan._portalId, "interestRate", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="EMI (₹)">
                <Input
                  type="number"
                  min="0"
                  value={loan.emi ?? ""}
                  onChange={(event) =>
                    update(loan._portalId, "emi", event.target.value)
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Outstanding Balance (₹)">
                <Input
                  type="number"
                  min="0"
                  value={loan.outstandingBalance ?? ""}
                  onChange={(event) =>
                    update(
                      loan._portalId,
                      "outstandingBalance",
                      event.target.value,
                    )
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Residual Tenure (Months)">
                <Input
                  type="number"
                  min="0"
                  value={loan.residualTenureMonths ?? ""}
                  onChange={(event) =>
                    update(
                      loan._portalId,
                      "residualTenureMonths",
                      event.target.value,
                    )
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField label="Disbursement Date">
                <Input
                  type="date"
                  value={loan.disbursementDate || ""}
                  onChange={(event) =>
                    update(
                      loan._portalId,
                      "disbursementDate",
                      event.target.value,
                    )
                  }
                  className="h-10 rounded-xl bg-white"
                />
              </FormField>

              <FormField
                label="Remarks"
                className="md:col-span-2 lg:col-span-3"
              >
                <Textarea
                  value={loan.remarks}
                  onChange={(event) =>
                    update(loan._portalId, "remarks", event.target.value)
                  }
                  rows={3}
                  className="rounded-xl bg-white"
                />
              </FormField>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={add}
        data-testid="add-loan"
        className="mt-5 rounded-full"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add a loan
      </Button>
    </StepShell>
  );
}
